import { useEffect, useRef, useState } from "react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/Delete";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

const API = "http://127.0.0.1:8000/IpVijil";

type RunMeta = {
  timestamp: string;
  name: string;
  claim_element?: string;
  sub_features?: string;
  model?: string;
  size?: string;
  quality?: string;
  cost?: number;
  tokens?: string;
  annotated_b64?: string;
  steps?: boolean;
  steps_b64?: string;
  step_cost?: number;
  step_tokens?: string;
  step_error?: string;
};

const Uploader = ({
  label,
  file,
  onFile,
}: {
  label: string;
  file: File | null;
  onFile: (f: File | null) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!file) return setPreview(null);
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div
      className="flex-1 min-w-0 border border-dashed border-[#CED2DA] rounded-xl bg-white hover:border-[#1751D0] transition-colors cursor-pointer relative overflow-hidden"
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f && f.type.startsWith("image/")) onFile(f);
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => onFile(e.target.files?.[0] || null)}
      />
      {preview ? (
        <div className="relative">
          <img src={preview} alt={label} className="w-full h-[260px] object-contain bg-[#FAFBFC]" />
          <button
            type="button"
            title="Remove"
            className="absolute top-2 right-2 bg-white border border-[#CED2DA] rounded-full p-1 text-[#667085] hover:text-[#B42318] shadow-sm"
            onClick={(e) => {
              e.stopPropagation();
              onFile(null);
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </button>
          <div className="absolute bottom-0 inset-x-0 bg-white/90 border-t border-[#E5E7EB] px-3 py-1.5 text-xs font-interMedium text-[#0A090B] truncate">
            {label} · {file?.name}
          </div>
        </div>
      ) : (
        <div className="h-[260px] flex flex-col items-center justify-center gap-2 text-[#667085]">
          <div className="w-12 h-12 rounded-full bg-[#F5F8FD] flex items-center justify-center">
            <CloudUploadOutlinedIcon sx={{ fontSize: 24, color: "#1751D0" }} />
          </div>
          <p className="text-sm font-interSemiBold text-[#0A090B]">{label}</p>
          <p className="text-xs font-interRegular">Click or drag an image here</p>
        </div>
      )}
    </div>
  );
};

const GptImageAnnotator = () => {
  const [patentFile, setPatentFile] = useState<File | null>(null);
  const [targetFile, setTargetFile] = useState<File | null>(null);
  const [claimElement, setClaimElement] = useState("");
  const [subFeatures, setSubFeatures] = useState("");
  const [profile, setProfile] = useState("");
  const [accused, setAccused] = useState("");
  const [figDesc, setFigDesc] = useState("");
  const [instruction, setInstruction] = useState("");
  const [showContext, setShowContext] = useState(false);
  const [model, setModel] = useState("gpt-image-2-2026-04-21");
  const [size, setSize] = useState("auto");
  const [quality, setQuality] = useState("low");
  const [stepByStep, setStepByStep] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<RunMeta | null>(null);
  const [history, setHistory] = useState<RunMeta[]>([]);
  const [openRun, setOpenRun] = useState<string | null>(null);
  const [viewRun, setViewRun] = useState<RunMeta | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    if (!viewRun) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setViewRun(null);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [viewRun]);

  const deleteRun = async (ts: string) => {
    if (!window.confirm("Delete this run from history?")) return;
    setDeleting(ts);
    try {
      await fetch(`${API}/gpt-image/runs/${ts}`, { method: "DELETE" });
      if (viewRun?.timestamp === ts) setViewRun(null);
      if (openRun === ts) setOpenRun(null);
      await loadHistory();
    } catch {
      /* leave the card; next reload will reconcile */
    } finally {
      setDeleting(null);
    }
  };

  const loadHistory = () =>
    fetch(`${API}/gpt-image/history`)
      .then((r) => r.json())
      .then((d) => setHistory(Array.isArray(d) ? d : []))
      .catch(() => {});
  useEffect(() => {
    loadHistory();
  }, []);

  const sessionCost = history.reduce((a, r) => a + (r.cost || 0), 0);

  const run = async () => {
    if (!patentFile || !targetFile || !claimElement.trim() || running) return;
    setRunning(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("patent", patentFile);
      fd.append("target", targetFile);
      fd.append("claim_element", claimElement);
      fd.append("sub_features", subFeatures);
      fd.append("functional_profile", profile);
      fd.append("accused_instrumentality", accused);
      fd.append("figure_description", figDesc);
      fd.append("instruction", instruction);
      fd.append("model", model);
      fd.append("size", size);
      fd.append("quality", quality);
      fd.append("step_by_step", String(stepByStep));
      const resp = await fetch(`${API}/gpt-image/annotate`, { method: "POST", body: fd });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(detail?.detail || `HTTP ${resp.status}`);
      }
      const data: RunMeta = await resp.json();
      setResult(data);
      loadHistory();
    } catch (e: any) {
      setError(String(e?.message || e));
    } finally {
      setRunning(false);
    }
  };

  const inputCls =
    "w-full border border-[#CED2DA] rounded-lg px-3 py-2 text-sm font-interRegular text-[#0A090B] focus:outline-none focus:border-[#1751D0] focus:ring-1 focus:ring-[#1751D0] bg-white";

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#1751D0] flex items-center justify-center">
              <AutoAwesomeIcon sx={{ fontSize: 20, color: "#fff" }} />
            </div>
            <div>
              <h1 className="text-lg font-interSemiBold text-[#0A090B]">
                GPT-Image Comparator & Annotator
              </h1>
              <p className="text-xs font-interRegular text-[#667085]">
                One image-model call — the hybrid comparison prompt drives annotations drawn
                directly on the target image
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-[#F5F8FD] border border-[#1751D0]/20 rounded-lg px-3 py-1.5">
            <PaidOutlinedIcon sx={{ fontSize: 16, color: "#1751D0" }} />
            <span className="text-sm font-interSemiBold text-[#1751D0]">
              ${sessionCost.toFixed(4)}
            </span>
            <span className="text-xs font-interRegular text-[#667085]">all runs</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Uploads */}
        <div className="flex gap-4 flex-col md:flex-row">
          <Uploader label="Patent figure" file={patentFile} onFile={setPatentFile} />
          <Uploader label="Target (accused instrumentality)" file={targetFile} onFile={setTargetFile} />
        </div>

        {/* Inputs card */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 space-y-4 shadow-sm">
          <div>
            <label className="block text-xs font-interSemiBold text-[#0A090B] mb-1.5 uppercase tracking-wide">
              Claim element (verbatim clause)
            </label>
            <textarea
              className={`${inputCls} h-24 resize-y`}
              placeholder="at least one satellite(100, 400, 500, 600, 800) having a plurality of sections ..."
              value={claimElement}
              onChange={(e) => setClaimElement(e.target.value)}
            />
          </div>
          <div className="flex gap-4 flex-col md:flex-row">
            <div className="flex-1">
              <label className="block text-xs font-interSemiBold text-[#0A090B] mb-1.5 uppercase tracking-wide">
                Sub-feature(s) to annotate
              </label>
              <input
                className={inputCls}
                placeholder="satellite(100, 400, 500, 600, 800) — defaults to the whole clause"
                value={subFeatures}
                onChange={(e) => setSubFeatures(e.target.value)}
              />
            </div>
            <div className="flex gap-3">
              <div>
                <label className="block text-xs font-interSemiBold text-[#0A090B] mb-1.5 uppercase tracking-wide">
                  Size
                </label>
                <select className={inputCls} value={size} onChange={(e) => setSize(e.target.value)}>
                  {["auto", "1024x1024", "1536x1024", "1024x1536"].map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-interSemiBold text-[#0A090B] mb-1.5 uppercase tracking-wide">
                  Quality
                </label>
                <select
                  className={inputCls}
                  value={quality}
                  onChange={(e) => setQuality(e.target.value)}
                >
                  {["high", "medium", "low"].map((q) => (
                    <option key={q}>{q}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <button
            type="button"
            className="flex items-center gap-1.5 text-sm font-interMedium text-[#1751D0]"
            onClick={() => setShowContext(!showContext)}
          >
            <ExpandMoreIcon
              sx={{
                fontSize: 18,
                transform: showContext ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
            Optional context
          </button>
          {showContext && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
              <textarea
                className={`${inputCls} h-20`}
                placeholder="Functional profile"
                value={profile}
                onChange={(e) => setProfile(e.target.value)}
              />
              <textarea
                className={`${inputCls} h-20`}
                placeholder="Accused instrumentality text"
                value={accused}
                onChange={(e) => setAccused(e.target.value)}
              />
              <textarea
                className={`${inputCls} h-20`}
                placeholder="Patent figure description"
                value={figDesc}
                onChange={(e) => setFigDesc(e.target.value)}
              />
              <div className="space-y-3">
                <input
                  className={inputCls}
                  placeholder="Extra instruction"
                  value={instruction}
                  onChange={(e) => setInstruction(e.target.value)}
                />
                <input
                  className={inputCls}
                  placeholder="Image model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer select-none w-fit">
            <input
              type="checkbox"
              checked={stepByStep}
              onChange={(e) => setStepByStep(e.target.checked)}
              className="w-4 h-4 accent-[#1751D0]"
            />
            <span className="text-sm font-interMedium text-[#0A090B]">
              Also generate a step-by-step breakdown
            </span>
            <span className="text-xs font-interRegular text-[#667085]">
              (second image mapping each match — doubles the image cost)
            </span>
          </label>

          <div className="flex items-center gap-4 pt-1">
            <button
              type="button"
              disabled={!patentFile || !targetFile || !claimElement.trim() || running}
              onClick={run}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-interSemiBold text-white transition-colors ${
                !patentFile || !targetFile || !claimElement.trim() || running
                  ? "bg-[#98A2B3] cursor-not-allowed"
                  : "bg-[#1751D0] hover:bg-[#123FA3]"
              }`}
            >
              {running ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                  Comparing & annotating…
                </>
              ) : (
                <>
                  <AutoAwesomeIcon sx={{ fontSize: 17 }} />
                  Compare & Annotate
                </>
              )}
            </button>
            {error && <p className="text-sm font-interMedium text-[#B42318]">{error}</p>}
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-base font-interSemiBold text-[#0A090B] flex items-center gap-2">
                <ImageOutlinedIcon sx={{ fontSize: 20, color: "#1751D0" }} />
                {result.name || "Result"}
              </h2>
              <div className="flex items-center gap-2">
                <span className="text-xs font-interMedium text-[#667085]">{result.tokens}</span>
                <span className="bg-[#ECFDF3] text-[#1D7C4D] text-sm font-interSemiBold px-3 py-1 rounded-full">
                  ${Number(result.cost || 0).toFixed(4)} this run
                </span>
                <a
                  href={`data:image/png;base64,${result.annotated_b64}`}
                  download={`${result.timestamp}_annotated.png`}
                  className="flex items-center gap-1.5 border border-[#CED2DA] hover:border-[#1751D0] text-[#1751D0] text-sm font-interMedium px-3 py-1.5 rounded-lg"
                >
                  <DownloadOutlinedIcon sx={{ fontSize: 16 }} /> Download
                </a>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <figure className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                {targetFile && (
                  <img
                    src={URL.createObjectURL(targetFile)}
                    alt="original"
                    className="w-full h-[380px] object-contain bg-[#FAFBFC]"
                  />
                )}
                <figcaption className="text-center text-xs font-interMedium text-[#667085] py-2 border-t border-[#E5E7EB]">
                  Original target
                </figcaption>
              </figure>
              <figure className="border-2 border-[#1751D0]/30 rounded-lg overflow-hidden">
                <img
                  src={`data:image/png;base64,${result.annotated_b64}`}
                  alt="annotated"
                  className="w-full h-[380px] object-contain bg-[#FAFBFC]"
                />
                <figcaption className="text-center text-xs font-interSemiBold text-[#1751D0] py-2 border-t border-[#E5E7EB] bg-[#F5F8FD]">
                  Annotated by GPT-Image
                </figcaption>
              </figure>
            </div>
            {result.steps_b64 && (
              <figure className="border-2 border-[#1D7C4D]/30 rounded-lg overflow-hidden">
                <img
                  src={`data:image/png;base64,${result.steps_b64}`}
                  alt="step-by-step"
                  className="w-full max-h-[560px] object-contain bg-[#FAFBFC]"
                />
                <figcaption className="flex items-center justify-center gap-3 text-xs font-interSemiBold text-[#1D7C4D] py-2 border-t border-[#E5E7EB] bg-[#ECFDF3]">
                  Step-by-step breakdown
                  {typeof result.step_cost === "number" && (
                    <span className="font-interMedium text-[#667085]">
                      ${result.step_cost.toFixed(4)}
                    </span>
                  )}
                  <a
                    href={`data:image/png;base64,${result.steps_b64}`}
                    download={`${result.timestamp}_steps.png`}
                    className="text-[#1751D0] font-interMedium"
                  >
                    Download
                  </a>
                </figcaption>
              </figure>
            )}
            {result.step_error && (
              <p className="text-sm font-interMedium text-[#B42318]">
                Step-by-step image failed: {result.step_error}
              </p>
            )}
          </div>
        )}

        {/* History */}
        {history.length > 0 && (
          <div className="space-y-3">
            <h2 className="text-base font-interSemiBold text-[#0A090B] flex items-center gap-2">
              <HistoryOutlinedIcon sx={{ fontSize: 20, color: "#667085" }} />
              History
              <span className="text-xs font-interMedium text-[#667085] bg-[#F2F4F7] rounded-full px-2 py-0.5">
                {history.length}
              </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {history.map((run) => (
                <div
                  key={run.timestamp}
                  className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:border-[#1751D0]/40 transition-all cursor-pointer"
                  onClick={() => setOpenRun(openRun === run.timestamp ? null : run.timestamp)}
                >
                  <img
                    src={`${API}/gpt-image/runs/${run.timestamp}/annotated`}
                    alt={run.name}
                    className="w-full h-[150px] object-contain bg-[#FAFBFC] border-b border-[#E5E7EB]"
                    loading="lazy"
                  />
                  <div className="px-3.5 py-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-interSemiBold text-[#0A090B] truncate">
                        {run.name}
                      </p>
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        {run.steps && (
                          <span className="text-[10px] font-interSemiBold text-[#1D7C4D] bg-[#ECFDF3] rounded-full px-1.5 py-0.5">
                            steps
                          </span>
                        )}
                        <span className="text-xs font-interSemiBold text-[#1D7C4D]">
                          ${Number(run.cost || 0).toFixed(4)}
                        </span>
                      </span>
                    </div>
                    <p className="text-[11px] font-interRegular text-[#667085]">
                      {run.timestamp.replace(
                        /(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/,
                        "$1-$2-$3 $4:$5"
                      )}
                      {run.quality ? ` · ${run.quality}` : ""}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <button
                        type="button"
                        title="View"
                        className="flex items-center gap-1 text-xs font-interMedium text-[#1751D0] border border-[#CED2DA] hover:border-[#1751D0] rounded-md px-2 py-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setViewRun(run);
                        }}
                      >
                        <VisibilityOutlinedIcon sx={{ fontSize: 14 }} /> View
                      </button>
                      <a
                        href={`${API}/gpt-image/runs/${run.timestamp}/annotated`}
                        download={`${run.timestamp}_annotated.png`}
                        title="Download annotated"
                        className="flex items-center gap-1 text-xs font-interMedium text-[#667085] border border-[#CED2DA] hover:border-[#1751D0] hover:text-[#1751D0] rounded-md px-2 py-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <DownloadOutlinedIcon sx={{ fontSize: 14 }} />
                      </a>
                      <button
                        type="button"
                        title="Delete run"
                        disabled={deleting === run.timestamp}
                        className="flex items-center gap-1 text-xs font-interMedium text-[#667085] border border-[#CED2DA] hover:border-[#B42318] hover:text-[#B42318] rounded-md px-2 py-1 ml-auto disabled:opacity-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteRun(run.timestamp);
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                      </button>
                    </div>
                    {openRun === run.timestamp && (
                      <div className="mt-2 pt-2 border-t border-[#F2F4F7] space-y-2">
                        <img
                          src={`${API}/gpt-image/runs/${run.timestamp}/original`}
                          alt="original"
                          className="w-full h-[120px] object-contain bg-[#FAFBFC] rounded"
                          onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                        />
                        {run.claim_element && (
                          <p className="text-[11px] font-interRegular text-[#475569] line-clamp-3">
                            {run.claim_element}
                          </p>
                        )}
                        <a
                          href={`${API}/gpt-image/runs/${run.timestamp}/annotated`}
                          download={`${run.timestamp}_annotated.png`}
                          className="inline-flex items-center gap-1 text-xs font-interMedium text-[#1751D0]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DownloadOutlinedIcon sx={{ fontSize: 14 }} /> Download annotated
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Run viewer modal */}
      {viewRun && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-6"
          style={{ zIndex: 1300 }}
          onClick={() => setViewRun(null)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-[#E5E7EB]">
              <div className="min-w-0">
                <p className="text-sm font-interSemiBold text-[#0A090B] truncate">
                  {viewRun.name}
                </p>
                <p className="text-[11px] font-interRegular text-[#667085]">
                  {viewRun.timestamp.replace(
                    /(\d{4})(\d{2})(\d{2})-(\d{2})(\d{2})(\d{2})/,
                    "$1-$2-$3 $4:$5:$6"
                  )}
                  {viewRun.model ? ` · ${viewRun.model}` : ""}
                  {viewRun.quality ? ` · ${viewRun.quality}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="bg-[#ECFDF3] text-[#1D7C4D] text-xs font-interSemiBold px-2.5 py-1 rounded-full">
                  ${Number(viewRun.cost || 0).toFixed(4)}
                </span>
                <a
                  href={`${API}/gpt-image/runs/${viewRun.timestamp}/annotated`}
                  download={`${viewRun.timestamp}_annotated.png`}
                  className="flex items-center gap-1 border border-[#CED2DA] hover:border-[#1751D0] text-[#1751D0] text-xs font-interMedium px-2.5 py-1.5 rounded-lg"
                >
                  <DownloadOutlinedIcon sx={{ fontSize: 14 }} /> Download
                </a>
                <button
                  type="button"
                  title="Delete run"
                  disabled={deleting === viewRun.timestamp}
                  className="flex items-center gap-1 border border-[#CED2DA] hover:border-[#B42318] text-[#667085] hover:text-[#B42318] text-xs font-interMedium px-2.5 py-1.5 rounded-lg disabled:opacity-50"
                  onClick={() => deleteRun(viewRun.timestamp)}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 14 }} /> Delete
                </button>
                <button
                  type="button"
                  title="Close"
                  className="p-1.5 rounded hover:bg-gray-100 text-[#667085]"
                  onClick={() => setViewRun(null)}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <figure className="border border-[#E5E7EB] rounded-lg overflow-hidden">
                  <img
                    src={`${API}/gpt-image/runs/${viewRun.timestamp}/original`}
                    alt="original"
                    className="w-full h-[52vh] object-contain bg-[#FAFBFC]"
                    onError={(e) => {
                      const fig = (e.target as HTMLImageElement).closest("figure");
                      if (fig) fig.style.display = "none";
                    }}
                  />
                  <figcaption className="text-center text-xs font-interMedium text-[#667085] py-2 border-t border-[#E5E7EB]">
                    Original target
                  </figcaption>
                </figure>
                <figure className="border-2 border-[#1751D0]/30 rounded-lg overflow-hidden">
                  <img
                    src={`${API}/gpt-image/runs/${viewRun.timestamp}/annotated`}
                    alt="annotated"
                    className="w-full h-[52vh] object-contain bg-[#FAFBFC]"
                  />
                  <figcaption className="text-center text-xs font-interSemiBold text-[#1751D0] py-2 border-t border-[#E5E7EB] bg-[#F5F8FD]">
                    Annotated by GPT-Image
                  </figcaption>
                </figure>
              </div>
              {viewRun.steps && (
                <figure className="mt-4 border-2 border-[#1D7C4D]/30 rounded-lg overflow-hidden">
                  <img
                    src={`${API}/gpt-image/runs/${viewRun.timestamp}/steps`}
                    alt="step-by-step"
                    className="w-full max-h-[52vh] object-contain bg-[#FAFBFC]"
                    onError={(e) => {
                      const fig = (e.target as HTMLImageElement).closest("figure");
                      if (fig) fig.style.display = "none";
                    }}
                  />
                  <figcaption className="flex items-center justify-center gap-3 text-xs font-interSemiBold text-[#1D7C4D] py-2 border-t border-[#E5E7EB] bg-[#ECFDF3]">
                    Step-by-step breakdown
                    <a
                      href={`${API}/gpt-image/runs/${viewRun.timestamp}/steps`}
                      download={`${viewRun.timestamp}_steps.png`}
                      className="text-[#1751D0] font-interMedium"
                    >
                      Download
                    </a>
                  </figcaption>
                </figure>
              )}
              {viewRun.claim_element && (
                <p className="mt-4 text-xs font-interRegular text-[#475569] bg-[#F8FAFC] border border-[#E5E7EB] rounded-lg px-3 py-2">
                  <span className="font-interSemiBold text-[#0A090B]">Claim: </span>
                  {viewRun.claim_element}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GptImageAnnotator;
